import { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Download, FileText, Settings2, Sparkles, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });
api.interceptors.request.use((c) => { const t = localStorage.getItem("feemitra_token"); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });
const inr = (n = 0) => `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const err = (x, d) => x.response?.data?.detail || d;

async function downloadInvoice(inv) {
  const res = await api.get(`/invoices/${inv.id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(res.data); const a = document.createElement("a");
  a.href = url; a.download = `${inv.invoice_no.replaceAll("/", "-")}.pdf`; document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
}

function Modal({ children, onClose, testid }) {
  return <div className="modal-backdrop"><div className="modal super-modal" data-testid={testid}><button type="button" className="icon-btn close" onClick={onClose} data-testid={`close-${testid}`}><X size={18} /></button>{children}</div></div>;
}

function Field({ label, k, f, setF, ...rest }) {
  return <label>{label}<input value={f[k] ?? ""} onChange={e => setF({ ...f, [k]: e.target.value })} data-testid={`billing-${k}-input`} {...rest} /></label>;
}

function useSaveForm(load, save, onDone) {
  const [f, setF] = useState(null); const [busy, setBusy] = useState(false); const [msg, setMsg] = useState("");
  useEffect(() => { load().then(setF); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async (e) => {
    e.preventDefault(); setMsg(""); setBusy(true);
    try { await save({ ...f, gstin: (f.gstin || "").toUpperCase().trim() }); onDone(); } catch (x) { setMsg(err(x, "Could not save")); } finally { setBusy(false); }
  };
  return { f, setF, busy, msg, submit };
}

function SellerModal({ onClose, onSaved }) {
  const { f, setF, busy, msg, submit } = useSaveForm(async () => (await api.get("/super/billing-settings")).data, (d) => api.put("/super/billing-settings", { ...d, gst_rate: Number(d.gst_rate) }), () => { onSaved(); onClose(); });
  if (!f) return null;
  return <Modal onClose={onClose} testid="seller-billing-modal"><form onSubmit={submit}>
    <p className="eyebrow">YOUR COMPANY</p><h3>Invoice details</h3><p className="muted">Printed on every GST invoice you issue to schools.</p>
    <div className="form-grid">
      <Field label="Legal name" k="legal_name" f={f} setF={setF} required />
      <Field label="GSTIN" k="gstin" f={f} setF={setF} placeholder="09ABCDE1234F1Z5" />
      <Field label="Address" k="address" f={f} setF={setF} />
      <Field label="State" k="state" f={f} setF={setF} placeholder="Uttar Pradesh" />
      <Field label="State code" k="state_code" f={f} setF={setF} placeholder="Auto from GSTIN" />
      <Field label="Email" k="email" f={f} setF={setF} />
      <Field label="Phone" k="phone" f={f} setF={setF} />
      <Field label="Invoice prefix" k="invoice_prefix" f={f} setF={setF} />
      <Field label="SAC code" k="sac_code" f={f} setF={setF} />
      <label className="toggle-row"><input type="checkbox" checked={!!f.gst_enabled} onChange={e => setF({ ...f, gst_enabled: e.target.checked })} data-testid="billing-gst-enabled-toggle" /> Charge GST on plans (turn on only after GST registration)</label>
      {f.gst_enabled && <Field label="GST rate (%)" k="gst_rate" f={f} setF={setF} type="number" min="0" max="28" step="0.5" />}
    </div>
    {msg && <div className="error-box" data-testid="seller-billing-error">{msg}</div>}
    <button className="primary-btn wide" disabled={busy} data-testid="save-seller-billing">{busy ? "Saving…" : "Save details"}</button>
  </form></Modal>;
}

export function SchoolBillingModal({ school, onClose, onSaved }) {
  const { f, setF, busy, msg, submit } = useSaveForm(async () => ({ billing_name: school.name, address: school.city, ...(school.billing || {}) }), (d) => api.put(`/super/schools/${school.id}/billing`, d), () => { onSaved(); onClose(); });
  if (!f) return null;
  return <Modal onClose={onClose} testid="school-billing-modal"><form onSubmit={submit}>
    <p className="eyebrow">BILL TO</p><h3>{school.name}</h3><p className="muted">Leave GSTIN blank if the school is not GST registered.</p>
    <div className="form-grid">
      <Field label="Billing name" k="billing_name" f={f} setF={setF} />
      <Field label="GSTIN" k="gstin" f={f} setF={setF} />
      <Field label="Address" k="address" f={f} setF={setF} />
      <Field label="State" k="state" f={f} setF={setF} />
      <Field label="State code" k="state_code" f={f} setF={setF} placeholder="e.g. 09" />
    </div>
    {msg && <div className="error-box" data-testid="school-billing-error">{msg}</div>}
    <button className="primary-btn wide" disabled={busy} data-testid="save-school-billing">{busy ? "Saving…" : "Save billing info"}</button>
  </form></Modal>;
}

function InvoiceTable({ invoices, actions }) {
  return <div className="table-wrap"><table><thead><tr><th>Invoice</th><th>School</th><th>Period</th><th>Taxable</th><th>GST</th><th>Total</th><th>Status</th><th>Action</th></tr></thead>
    <tbody>{invoices.map(i => <tr key={i.id} data-testid={`invoice-row-${i.id}`}>
      <td><strong>{i.invoice_no}</strong><small>{i.plan_name} plan</small></td>
      <td>{i.school_name}<small>{i.buyer?.gstin || "Unregistered"}</small></td>
      <td>{i.month}</td>
      <td>{inr(i.taxable)}</td>
      <td>{i.gst_rate > 0 ? <>{inr(i.cgst + i.sgst + i.igst)}<small>{i.intra_state ? "CGST + SGST" : "IGST"}</small></> : <small>No GST</small>}</td>
      <td><strong data-testid={`invoice-total-${i.id}`}>{inr(i.total)}</strong></td>
      <td><span className={`status ${i.status === "paid" ? "paid" : "pending"}`} data-testid={`invoice-status-${i.id}`}>{i.status}</span></td>
      <td><div className="row-actions">
        <button className="pay-link" onClick={() => downloadInvoice(i)} data-testid={`invoice-pdf-${i.id}`}><Download size={13} /> PDF</button>
        {actions?.(i)}
      </div></td>
    </tr>)}</tbody></table>
    {!invoices.length && <div className="empty-state" data-testid="invoices-empty">No invoices yet.</div>}
  </div>;
}

export function SuperInvoices({ notify }) {
  const [month, setMonth] = useState(thisMonth()); const [invoices, setInvoices] = useState([]);
  const [showSeller, setShowSeller] = useState(false); const [busy, setBusy] = useState("");
  const load = async () => setInvoices((await api.get("/super/invoices", { params: { month } })).data);
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const generate = async () => {
    setBusy("gen");
    try { const { data } = await api.post("/super/invoices/generate", { month }); notify(`${data.created} invoice(s) created, ${data.skipped} skipped`); load(); }
    catch (x) { notify(err(x, "Could not generate")); if (x.response?.status === 400 && /billing details/.test(x.response.data.detail)) setShowSeller(true); }
    finally { setBusy(""); }
  };
  const toggle = async (i) => {
    setBusy(i.id);
    try { await api.post(`/super/invoices/${i.id}/status`, { status: i.status === "paid" ? "unpaid" : "paid" }); load(); } finally { setBusy(""); }
  };
  const due = invoices.filter(i => i.status !== "paid").reduce((a, i) => a + i.total, 0);
  return <section className="table-section">
    <div className="table-toolbar invoice-toolbar">
      <div className="section-title" style={{ margin: 0 }}><div><h2>GST invoices</h2><p className="muted" data-testid="invoices-summary">{invoices.length} invoice(s) · {inr(due)} unpaid</p></div></div>
      <div className="row-actions">
        <input type="month" className="month-input" value={month} onChange={e => setMonth(e.target.value)} data-testid="invoice-month-input" />
        <button className="secondary-btn" onClick={() => setShowSeller(true)} data-testid="seller-billing-button"><Settings2 size={14} /> Company details</button>
        <button className="primary-btn small" onClick={generate} disabled={busy === "gen"} data-testid="generate-invoices-button"><Sparkles size={14} /> {busy === "gen" ? "Generating…" : "Generate"}</button>
      </div>
    </div>
    <InvoiceTable invoices={invoices} actions={(i) => <button className="pay-link" onClick={() => toggle(i)} disabled={busy === i.id} data-testid={`invoice-toggle-${i.id}`}><CheckCircle2 size={13} /> {i.status === "paid" ? "Mark unpaid" : "Mark paid"}</button>} />
    {showSeller && <SellerModal onClose={() => setShowSeller(false)} onSaved={() => notify("Company details saved")} />}
  </section>;
}

export function SchoolInvoices() {
  const [invoices, setInvoices] = useState(null);
  useEffect(() => { api.get("/billing/invoices").then(r => setInvoices(r.data)); }, []);
  if (!invoices) return null;
  return <section className="table-section" data-testid="school-invoices">
    <div className="table-toolbar"><div className="section-title" style={{ margin: 0 }}><div><h2><FileText size={15} /> FeeMitra subscription invoices</h2><p className="muted">Invoices for your FeeMitra plan. Download for your accounts.</p></div></div></div>
    <InvoiceTable invoices={invoices} />
  </section>;
}
